import { Component, computed, effect, inject, model, output, viewChild } from '@angular/core';
import {
  ActionListItemModel,
  ActionMenuItemAction,
  AMOUNT_DECIMALS,
  AMOUNT_FORMAT,
  Article,
  ArticleService,
  CapitalizePipe,
  ClientCreditNoteDraftItem,
  ClientCreditNoteDraftItemService,
  ClientCreditNoteItem,
  ClientInvoice,
  ClientInvoiceItem,
  DeleteDialogComponent,
  DialogResult,
  errorToast,
  getMergedListForIri,
  linkTypeMap,
  NullableKeys,
  RcSelectableOverflowDirective,
  RouteEntitySubscriberService,
  StoredSupplierCreditNoteItemLink,
  successDeleteToast,
  successSaveToast,
  SupplierCreditNote,
  SupplierCreditNoteItem,
  SupplierCreditNoteItemMapping,
  SupplierCreditNoteItemMappingsService,
  SupplierCreditNoteItemService,
  SupplierCreditNoteMappingType,
  SupplierInvoice,
  SupplierInvoiceItem,
  TableConfig,
  TableModalComponent,
  ValidationDirective,
  DropdownLoaderDirective,
} from 'shared';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, NgClass, UpperCasePipe } from '@angular/common';
import { ButtonComponent, DropDownButtonComponent } from '@progress/kendo-angular-buttons';
import { debounceTime, Observable, of, Subject, take } from 'rxjs';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NumberFormatOptions, NumberPipe } from '@progress/kendo-angular-intl';
import { distinctUntilChanged } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FormFieldComponent, NumericTextBoxComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import {
  SupplierCreditNoteLinkClientModalComponent
} from '@features/purchase/supplier_credit_notes/supplier-credit-note-details/supplier-credit-note-item/supplier-credit-note-link-client-modal/supplier-credit-note-link-client-modal.component';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  DraftItemFormManager
} from '@features/purchase/supplier_credit_notes/supplier-credit-note-details/supplier-credit-note-setup-and-helpers/DraftItemFormManager/client-credit-note.draft-item-form.manager';
import { ComboBoxComponent, DropDownFilterSettings, FilterDirective } from '@progress/kendo-angular-dropdowns';

@Component({
  selector: 'rc-supplier-credit-note-item',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    DropDownButtonComponent,
    NumberPipe,
    ButtonComponent,
    CapitalizePipe,
    TranslateModule,
    UpperCasePipe,
    TextBoxComponent,
    ValidationDirective,
    FormFieldComponent,
    NumericTextBoxComponent,
    NgClass,
    ComboBoxComponent,
    FilterDirective,
    TableModalComponent,
    RcSelectableOverflowDirective,
    DropdownLoaderDirective,
  ],
  providers: [CapitalizePipe, TranslatePipe, TranslateService, DialogRef],
  templateUrl: './supplier-credit-note-item.component.html',
  styleUrl: './supplier-credit-note-item.component.scss',
})
export class SupplierCreditNoteItemComponent {
  #router = inject(Router);
  #dialog = inject(DialogRef);
  #tracker = inject(RouteEntitySubscriberService);
  #dialogService = inject(DialogService);
  #capitalizePipe = inject(CapitalizePipe);
  #articleService = inject(ArticleService);
  #translateService = inject(TranslateService);
  #notificationService = inject(NotificationService);
  #supplierCreditNoteItemService = inject(SupplierCreditNoteItemService);
  #supplierCreditNoteItemMappingsService = inject(SupplierCreditNoteItemMappingsService);
  #clientCreditNoteDraftItemService = inject(ClientCreditNoteDraftItemService);

  #filterSubject = new Subject<{
    search: string;
    client: string;
    draftItemId: string;
    type: 'articles';
  }>();

  modals = viewChild(TableModalComponent);

  supplierCreditNote = model.required<SupplierCreditNote>();
  supplierCreditNoteItem = model.required<SupplierCreditNoteItem>();
  supplierCreditNoteItemMappings = model.required<SupplierCreditNoteItemMapping[]>();
  clientCreditNoteDraftItems = model.required<ClientCreditNoteDraftItem[]>();
  clientCreditNoteItems = model.required<ClientCreditNoteItem[]>();
  supplierCreditNoteIsLocked = model.required<boolean>();
  readonly = model.required<boolean>();

  itemIsDoNotCredit = computed(() => this.supplierCreditNoteItem().doNotCredit);
  itemLocked = computed(() => this.supplierCreditNoteIsLocked() || this.itemIsDoNotCredit() || this.readonly());

  update = output<boolean | string>();

  protected menuItems = computed<ActionListItemModel<ActionMenuItemAction>[]>(() => {
    const doNotCredit = this.supplierCreditNoteItem()?.doNotCredit;
    const text = doNotCredit ? 'DO_CREDIT' : 'DO_NOT_CREDIT';
    const action = doNotCredit ? ActionMenuItemAction.DO_CREDIT : ActionMenuItemAction.DO_NOT_CREDIT;
    const iconClass = doNotCredit ? 'rc-icon-credit-note' : 'rc-icon-no-invoice';
    return [
      {
        text: this.#capitalizePipe.transform(this.#translateService.instant(text)),
        action: action,
        iconClass: iconClass,
      },
    ];
  });
  articlesByDraftItem$ = new Map<string, Observable<Article[]>>();

  #draftItemFormManager = new DraftItemFormManager();
  draftItemForms = this.#draftItemFormManager.getAll() as unknown as Map<string, FormGroup>;

  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    maximumFractionDigits: 3,
    minimumFractionDigits: 2,
  };
  protected readonly amountFormat = AMOUNT_FORMAT;
  protected readonly amountDecimals = AMOUNT_DECIMALS;

  filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  tableConfig: TableConfig<ClientInvoice | SupplierInvoice | ClientInvoiceItem | SupplierInvoiceItem> = {
    entityTranslationKey: '',
    url: '',
    subscribeToItems: false,
    pageable: false,
    sortable: false,
    filterable: false,
    colAmount: 7,
  };

  constructor() {
    effect(() => {
      const item = this.supplierCreditNoteItem();
      if (!item) return;

      this.#syncFromDraftItems();
    });

    effect(() => {
      this.clientCreditNoteDraftItems();
      this.#syncFromDraftItems();
    });

    effect(() => {
      this.#draftItemFormManager.setLocked(this.itemLocked());
    });

    effect(() => {
      const draftItems = this.clientCreditNoteDraftItems();
      for (const draftItem of draftItems) {
        if (!this.articlesByDraftItem$.has(draftItem['@id']!) && draftItem.client) {
          const articles = getMergedListForIri(
            () => this.#articleService.getArticlesForClient(draftItem.client),
            () => this.#articleService.findArticles(`normalizedNumber=${draftItem.articleNumber}`),
          );
          this.articlesByDraftItem$.set(draftItem['@id']!, articles);
        }
      }
    });

    this.#filterSubject.pipe(debounceTime(300)).subscribe((value) => {
      if (value.type === 'articles') {
        this.filterArticles(value.search, value.client, value.draftItemId);
      }
    });
  }

  getDraftItemForMapping(mapping: SupplierCreditNoteItemMapping) {
    return this.clientCreditNoteDraftItems().find((di) => di['@id'] === mapping.clientCreditNoteDraftItem);
  }

  getLinkedItemForMapping(mapping: SupplierCreditNoteItemMapping) {
    return this.clientCreditNoteItems().find((ci) => ci['@id'] === mapping.clientCreditNoteItem);
  }

  linkItemToExistingMapping(mapping: SupplierCreditNoteItemMapping) {
    const storedLink = localStorage.getItem('supplierCreditNoteLink');
    if (!storedLink) return;

    const link = JSON.parse(storedLink) as StoredSupplierCreditNoteItemLink;
    if (
      link.documentType === SupplierCreditNoteMappingType.CLIENT_CREDIT_NOTE ||
      link.itemType === SupplierCreditNoteMappingType.CLIENT_CREDIT_NOTE
    )
      return;

    // remove existing links
    mapping.clientInvoice = null;
    mapping.clientInvoiceItem = null;
    mapping.supplierInvoice = null;
    mapping.supplierInvoiceItem = null;

    mapping[link.documentType] = link.documentIri;
    if (link.itemType && link.itemIri) mapping[link.itemType] = link.itemIri;

    this.#supplierCreditNoteItemMappingsService
      .patch(mapping['@id']!, mapping)
      .pipe(take(1), distinctUntilChanged())
      .subscribe({
        next: () => {
          localStorage.removeItem('supplierCreditNoteLink');
          this.update.emit(this.supplierCreditNoteItem()['@id']!);
          this.#tracker.broadcastItemTopicChanged('supplierCreditNote', mapping['@id']!);
          this.#notifySuccess(this.#translateService.instant('LINK'));
        },
        error: (error) => this.#notifyError(error),
      });
  }

  createNewMapping() {
    const storedLink = localStorage.getItem('supplierCreditNoteLink');
    if (!storedLink) return;

    const link = JSON.parse(storedLink) as StoredSupplierCreditNoteItemLink;
    const creditNoteItem = this.supplierCreditNoteItem();
    const mapping = { supplierCreditNoteItem: creditNoteItem['@id']!, [link.documentType]: link.documentIri };
    if (link.itemType && link.itemIri) mapping[link.itemType] = link.itemIri;

    if (link.itemType === SupplierCreditNoteMappingType.CLIENT_INVOICE_ITEM) {
      this.#createMapping(mapping as SupplierCreditNoteItemMapping);
    } else {
    }
  }

  openLinkClientDialog() {
    this.#dialog = this.#dialogService.open({
      content: SupplierCreditNoteLinkClientModalComponent,
      title: this.#capitalizePipe.transform(
        this.#translateService.instant('SELECT_ENTITY', { entity: this.#translateService.instant('CLIENT') }),
      ),
    });

    this.#dialog.result.subscribe((result: DialogResult) => {
      if (result.isSuccessFull && result.result) {
        const client = result.result as string;
        this.#linkClient(client);
      }
    });
  }

  openItemMappingDeleteDialog(itemMapping: SupplierCreditNoteItemMapping) {
    this.#dialog = this.#dialogService.open({
      content: DeleteDialogComponent,
      title: this.#capitalizePipe.transform(
        this.#translateService.instant('DELETE_DIALOG_TITLE', { value: this.#translateService.instant('ASSOCIATION') }),
      ),
    });

    this.#dialog.content.instance.value = this.#translateService.instant('THIS_ASSOCIATION');
    this.#dialog.result.subscribe((value: DialogResult) => {
      if (value.delete) {
        this.#deleteItemMapping(itemMapping!['@id']!);
      }
    });
  }

  #syncFromDraftItems() {
    this.#draftItemFormManager.syncFromDraftItems(this.clientCreditNoteDraftItems(), this.itemLocked());
  }

  #linkClient(client: string) {
    const itemIri = this.supplierCreditNoteItem()['@id']!;
    this.#supplierCreditNoteItemMappingsService
      .createWithClient({ client: client, supplierCreditNoteItem: itemIri })
      .pipe(take(1), distinctUntilChanged())
      .subscribe({
        next: () => {
          this.update.emit(itemIri);
          this.#tracker.broadcastItemTopicChanged('supplierCreditNote', itemIri);
          // this.#notifySuccess(this.#translateService.instant('LINK_SUCCESS'));
        },
        error: (error) => this.#notifyError(error),
      });
  }

  #createMapping(mapping: SupplierCreditNoteItemMapping) {
    this.#supplierCreditNoteItemMappingsService
      .create(mapping)
      .pipe(take(1), distinctUntilChanged())
      .subscribe({
        next: () => {
          this.update.emit(this.supplierCreditNoteItem()['@id']!);
          this.#tracker.broadcastItemTopicChanged('supplierCreditNote', this.supplierCreditNoteItem()['@id']!);
          this.#notifySuccess(this.#translateService.instant('LINK'));
          localStorage.removeItem('supplierCreditNoteLink');
        },
        error: (error) => this.#notifyError(error),
      });
  }

  #deleteItemMapping(itemMappingIri: string) {
    this.#supplierCreditNoteItemMappingsService
      .delete(itemMappingIri)
      .pipe(take(1), distinctUntilChanged())
      .subscribe({
        next: (response) => {
          this.update.emit(true);
          this.#syncFromDraftItems();
          this.#notifyDelete(this.#translateService.instant('ASSOCIATION'));
        },
        error: (error) => this.#notifyError(error),
      });
  }

  navigateToClient(clientIri: string) {
    const url = this.#router.serializeUrl(this.#router.createUrlTree([clientIri]));
    window.open(url, '_blank');
  }

  onSearchArticles(search: string, draftItem: ClientCreditNoteDraftItem) {
    this.#filterSubject.next({
      search,
      client: draftItem.client!,
      draftItemId: draftItem['@id']!,
      type: 'articles',
    });
  }

  filterArticles(search: string, client: string, draftItemId: string) {
    const stream$ = this.#articleService.getArticlesForClient(client, `normalizedNumber=${search}`);
    this.articlesByDraftItem$.set(draftItemId, stream$);
  }

  onDraftItemFormBlur(draftItem: ClientCreditNoteDraftItem, field: string) {
    const form = this.draftItemForms.get(draftItem['@id']!);
    if (!form || form.invalid) return;

    const value = form.get(field)?.value;
    if (value === null || value === draftItem[field as keyof ClientCreditNoteDraftItem]) return;

    this.#clientCreditNoteDraftItemService
      .update(draftItem['@id']!, { ...draftItem, [field]: value })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.#notifySuccess(this.#translateService.instant('SUPPLIER_CREDIT_NOTE'));
        },
        error: (error) => this.#notifyError(error),
      });
  }

  onActionMenuItemClick(actionMenuItem: ActionListItemModel<ActionMenuItemAction | string>) {
    switch (actionMenuItem.action) {
      case ActionMenuItemAction.DO_NOT_CREDIT:
        this.#doNotCreditChange(true);
        break;
      case ActionMenuItemAction.DO_CREDIT:
        this.#doNotCreditChange(false);
        break;
    }
  }

  #doNotCreditChange(value: boolean) {
    const itemIri = this.supplierCreditNoteItem()['@id']!;
    this.#supplierCreditNoteItemService
      .patch(itemIri, { doNotCredit: value })
      .pipe(take(1))
      .subscribe({
        next: () => this.update.emit(itemIri),
        error: (error) => this.#notifyError(error),
      });
  }

  openLinkModal(type: SupplierCreditNoteMappingType, mapping: SupplierCreditNoteItemMapping) {
    let modal, entityKey, linkIri;
    switch (type) {
      case SupplierCreditNoteMappingType.CLIENT_INVOICE:
        modal = this.modals()?.clientInvoiceModal();
        linkIri = mapping.clientInvoice;
        entityKey = 'client_invoice';
        break;
      case SupplierCreditNoteMappingType.SUPPLIER_INVOICE:
        modal = this.modals()?.supplierInvoiceModal();
        linkIri = mapping.supplierInvoice;
        entityKey = 'supplier_invoice';
        break;
      case SupplierCreditNoteMappingType.CLIENT_INVOICE_ITEM:
        modal = this.modals()?.clientInvoiceItemModal();
        linkIri = mapping.clientInvoiceItem;
        entityKey = 'client_invoice_item';
        break;
      case SupplierCreditNoteMappingType.SUPPLIER_INVOICE_ITEM:
        modal = this.modals()?.supplierInvoiceItemModal();
        linkIri = mapping.supplierInvoiceItem;
        entityKey = 'supplier_invoice_item';
        break;
    }

    if (modal) {
      this.#setupTableConfigForModal(entityKey!, linkIri ?? undefined, mapping);
      this.#dialog = this.#dialogService.open({
        content: modal,
        title: this.#capitalizePipe.transform(
          this.#translateService.instant('LINKED_ENTITY', {
            entity: this.#translateService.instant(entityKey?.toUpperCase() + 'S'),
          }),
        ),
        width: '1200px',
      });
    }
  }

  getLinkTypes(mapping: SupplierCreditNoteItemMapping) {
    const type =
      (mapping.clientInvoiceItem && SupplierCreditNoteMappingType.CLIENT_INVOICE_ITEM) ||
      (mapping.supplierInvoiceItem && SupplierCreditNoteMappingType.SUPPLIER_INVOICE_ITEM) ||
      (mapping.clientInvoice && SupplierCreditNoteMappingType.CLIENT_INVOICE) ||
      (mapping.supplierInvoice && SupplierCreditNoteMappingType.SUPPLIER_INVOICE);

    return type ? linkTypeMap[type] : null;
  }

  #setupTableConfigForModal(entityKey: string, linkIri?: string, mapping?: SupplierCreditNoteItemMapping) {
    const pluralEntityKey = entityKey + 's';
    this.tableConfig.entityTranslationKey = pluralEntityKey.toUpperCase();

    if (linkIri) {
      this.tableConfig.url = linkIri;
    }

    this.tableConfig.actionMenuItems$ = this.#getModalActionMenuItems(entityKey.toUpperCase());
    this.tableConfig.onActionMenuItemClick = (menuItem, record, propField) => {
      this.#onModalActionMenuClick(menuItem, record, propField, mapping);
    };
  }

  #getModalActionMenuItems(entityTranslationKey: string): Observable<ActionListItemModel<ActionMenuItemAction>[]> {
    return of([
      {
        text: this.#capitalizePipe.transform(
          this.#translateService.instant('UNLINK_ENTITY', {
            entity: this.#translateService.instant(entityTranslationKey),
          }),
        ),
        action: ActionMenuItemAction.DISASSOCIATE,
        iconClass: 'rc-icon-link-off',
      },
    ]);
  }

  #onModalActionMenuClick(
    actionMenuItem: ActionListItemModel<ActionMenuItemAction | string>,
    record: string,
    propField: string,
    mapping?: SupplierCreditNoteItemMapping,
  ) {
    switch (actionMenuItem.action) {
      case ActionMenuItemAction.DISASSOCIATE:
        this.#removeLinkedItem(record, propField as NullableKeys<SupplierCreditNoteItemMapping>, mapping);
        break;
    }
  }

  #removeLinkedItem(
    record: string,
    propField: NullableKeys<SupplierCreditNoteItemMapping>,
    mapping?: SupplierCreditNoteItemMapping,
  ) {
    if (!mapping) return;

    if (mapping[propField] === record) {
      mapping[propField] = null;
      // If document link also remove item
      if (
        propField === SupplierCreditNoteMappingType.CLIENT_INVOICE ||
        propField === SupplierCreditNoteMappingType.SUPPLIER_INVOICE
      ) {
        mapping[(propField + 'Item') as NullableKeys<SupplierCreditNoteItemMapping>] = null;
      }
      this.#supplierCreditNoteItemMappingsService
        .patch(mapping['@id']!, mapping)
        .pipe(take(1), distinctUntilChanged())
        .subscribe({
          next: () => {
            this.modals()
              ?.table()
              ?.removeItems((item) => item['@id'] === record);
            this.#notifySuccess(this.#translateService.instant('SUPPLIER_CREDIT_NOTE'));
          },
          error: (error) => this.#notifyError(error),
        });
    }
  }

  #notifySuccess(message: string, plural = false) {
    const n = this.#notificationService.show(successSaveToast);
    if (n.content) {
      n.content.instance.value = message;
      (n.content.instance as any).plural = plural;
    }
  }

  #notifyDelete(message: string) {
    const n = this.#notificationService.show(successDeleteToast);
    if (n.content) n.content.instance.value = message;
  }

  #notifyError(error: any) {
    const n = this.#notificationService.show(errorToast);
    if (n.content) n.content.instance.setCurrentValue(error);
  }

  close() {
    this.#dialog.close();
  }
}
